import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from './api'
import { useNavigate } from 'react-router'
import type { ShownPerson } from './peopleList'

// One-to-one connections (worker/connections.ts): people you aren't in a circle with, in each other's Everyone once
// one asks and the other accepts.
export type Account = { id: string; name: string; portrait: string | null }
export type ConnectionRequest = {
  id: string
  note: string | null
  at: number
  from: Account
  // When they joined gatheredfrom, as YYYY-MM-DD.
  joined: string
  // The names of people you both know.
  mutual: string[]
  // They opened your connect link.
  link?: boolean
}
// One you asked: by email it shows only the address you typed until they accept.
export type WaitingRequest = { id: string; at: number; to: { email: string } | Account }
export type Suggestion = Account & { mutual: string[] }
export type Connections = {
  requests: ConnectionRequest[]
  waiting: WaitingRequest[]
  connected: (Account & { since: number })[]
  suggestions: Suggestion[]
}

export const useConnections = () =>
  useQuery({ queryKey: ['connections'], queryFn: () => api<Connections>('/me/connections') })

// Anything that changes who you're connected to changes your People, Everyone and whether Everyone is offered.
export function useRefreshConnections() {
  const queryClient = useQueryClient()
  return () =>
    Promise.all(
      ['connections', 'me', 'my-people', 'everyone', 'find-people', 'account'].map((key) =>
        queryClient.invalidateQueries({ queryKey: [key] }),
      ),
    )
}

// Someone Search found: whether you're connected already, asked them, or they asked you.
export type Found = Suggestion & { status: 'connected' | 'asked' | 'asks' | null }

export const useFindPeople = (q: string, enabled: boolean) =>
  useQuery({
    queryKey: ['find-people', q],
    queryFn: async () => (await api<{ people: Found[] }>(`/me/connections/search?q=${encodeURIComponent(q)}`)).people,
    enabled,
    placeholderData: (previous) => previous,
  })

export type Asked = { ok?: boolean; already?: boolean; connected?: boolean }

export function useAsk() {
  const refresh = useRefreshConnections()
  return useMutation({
    mutationFn: (body: { to: string; note?: string } | { email: string; note?: string }) =>
      api<Asked>('/me/connections/requests', { method: 'POST', body }),
    onSuccess: refresh,
  })
}

export function useAnswer() {
  const refresh = useRefreshConnections()
  return useMutation({
    mutationFn: ({ id, yes }: { id: string; yes: boolean }) =>
      api(`/me/connections/requests/${id}/${yes ? 'accept' : 'decline'}`, { method: 'POST' }),
    onSuccess: refresh,
  })
}

export function useTakeBack() {
  const refresh = useRefreshConnections()
  return useMutation({
    mutationFn: (id: string) => api(`/me/connections/requests/${id}`, { method: 'DELETE' }),
    onSuccess: refresh,
  })
}

export function useDisconnect() {
  const refresh = useRefreshConnections()
  return useMutation({
    mutationFn: (userId: string) => api(`/me/connections/${userId}`, { method: 'DELETE' }),
    onSuccess: refresh,
  })
}

// "Rose and Lee", "Rose, Lee and 3 others": who you both know, in a few words.
export function bothKnow(names: string[]) {
  if (names.length === 0) return ''
  if (names.length === 1) return names[0]!
  if (names.length === 2) return `${names[0]} and ${names[1]}`
  const rest = names.length - 2
  return `${names[0]}, ${names[1]} and ${rest} ${rest === 1 ? 'other' : 'others'}`
}

// ---- Everyone

export type FeedItem = {
  kind: 'photo' | 'moment' | 'recipe' | 'album' | 'book' | 'cookbook'
  id: string
  at: number
  by: Account & { you: boolean }
  title: string
  text: string
  photo: { ref: string; width: number; height: number } | null
  photoKind: 'album-photos' | 'moment-photos' | 'photos' | null
  photoCount: number
}

// Where each kind of thing opens.
export const feedLink = (x: Pick<FeedItem, 'kind' | 'id'>) =>
  ({
    photo: `/photo/${x.id}`,
    moment: `/moment/${x.id}`,
    recipe: `/recipe/${x.id}`,
    album: `/album/${x.id}`,
    book: `/book/${x.id}`,
    cookbook: `/cookbook/${x.id}`,
  })[x.kind]

// ---- Your connect link, and your choices

export type MadeLink = { token: string; expiresAt: number }
export const connectUrl = (token: string) => `${location.origin}/connect/${token}`

export function useMakeLink() {
  return useMutation({ mutationFn: () => api<MadeLink>('/me/connect-links', { method: 'POST' }) })
}

export function useTurnOffLinks() {
  return useMutation({ mutationFn: () => api('/me/connect-links', { method: 'DELETE' }) })
}

// A Public profile can be found by anyone; `open` is "Anyone can see my stuff", only ever true while Public.
export type ConnectionSettings = { public: boolean; open: boolean; email: boolean }

export const useConnectionSettings = () =>
  useQuery({
    queryKey: ['connection-settings'],
    queryFn: () => api<ConnectionSettings>('/me/connection-settings'),
  })

export function useSaveConnectionSettings() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: Partial<ConnectionSettings>) => api('/me/connection-settings', { method: 'PATCH', body }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['connection-settings'] }),
  })
}

// Someone's link, as the person who opened it sees it. face: they have one, served from the link itself (linkFace).
export type LinkView = {
  id: string
  name: string
  state: 'you' | 'connected' | 'asked' | 'open'
  mutual: string[]
  face: boolean
}
export const linkFace = (token: string, variant: 'display' | 'thumb' = 'display') =>
  `/api/connect/${encodeURIComponent(token)}/face/${variant}`

// Someone's link. While you're waiting on them (asked), it's checked every few seconds, so their yes shows up here.
export const useLinkView = (token: string, { watch = false }: { watch?: boolean } = {}) =>
  useQuery({
    queryKey: ['connect-link', token],
    queryFn: () => api<LinkView>(`/connect/${encodeURIComponent(token)}`),
    enabled: !!token,
    retry: false,
    refetchInterval: (q) => (watch && q.state.data?.state === 'asked' ? WATCH_MS : false),
  })

// How often an open screen that's waiting on the other person checks: often enough to feel live with them standing
// right there, and only while the screen is open and the app in front.
export const WATCH_MS = 2000

// Requests waiting for you, checked every few seconds while your connect link is on screen.
export const useIncoming = (watch: boolean) =>
  useQuery({
    queryKey: ['connections-incoming'],
    queryFn: () => api<{ requests: ConnectionRequest[] }>('/me/connections/incoming'),
    enabled: watch,
    refetchInterval: watch ? WATCH_MS : false,
  })

// What a scanned QR code is, when it's one of ours: someone's connect link, or an invite to a circle. Only codes for
// this site count; anything else is left alone.
export type Scanned = { kind: 'connect'; token: string } | { kind: 'join'; token: string }
export function readScan(text: string, origin: string): Scanned | null {
  let url: URL
  try {
    url = new URL(text.trim())
  } catch {
    return null
  }
  if (url.origin !== origin) return null
  const found = /^\/(connect|join)\/([A-Za-z0-9_-]{43})\/?$/.exec(url.pathname)
  if (!found) return null
  return { kind: found[1] as Scanned['kind'], token: found[2]! }
}

// Inviting someone you've just connected with to one of your circles. They get it like any invite: in the app (Home,
// or the screen you connected on) and by email.
export function useInviteToCircle() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ circleId, userId }: { circleId: string; userId: string }) =>
      api(`/families/${encodeURIComponent(circleId)}/invites`, { method: 'POST', body: { via: 'account', userId } }),
    onSuccess: (_, { circleId }) => queryClient.invalidateQueries({ queryKey: ['family', circleId] }),
  })
}

// ---- Someone's own page (worker/connections.ts /accounts/:id)

export type AccountSection = 'photos' | 'moments' | 'recipes'
export type AccountProfile = Account & {
  you: boolean
  status: Found['status']
  // Connected one to one, so it can be ended.
  direct: boolean
  mutual: string[]
  // The circles you're both in.
  circles: { id: string; name: string }[]
  // Their sections that hold something you can see.
  sections: AccountSection[]
}

export const accountLink = (userId: string) => `/profile/${userId}`

export const useAccount = (id: string) =>
  useQuery({ queryKey: ['account', id], queryFn: () => api<AccountProfile>(`/accounts/${id}`) })

export const useAccountItems = (id: string, section: AccountSection | null, enabled: boolean) =>
  useInfiniteQuery({
    queryKey: ['account', id, 'items', section],
    queryFn: ({ pageParam }) => {
      const q = new URLSearchParams()
      if (section) q.set('section', section)
      if (pageParam) q.set('before', pageParam)
      return api<{ items: FeedItem[]; next: string | null }>(`/accounts/${id}/items?${q}`)
    },
    initialPageParam: '',
    getNextPageParam: (last) => last.next ?? undefined,
    enabled,
  })

// Where tapping someone on People goes: straight to their page (on the family tree when they're on it, else
// everything of theirs you can see), or null for someone with neither, who opens the profile sheet instead (mobile
// review, Dallas 2026-10-18).
export const pageOf = (person: Pick<ShownPerson, 'href' | 'userId'>) =>
  person.href ?? (person.userId ? accountLink(person.userId) : null)

export function useOpenPerson(showProfile: (person: ShownPerson) => void) {
  const navigate = useNavigate()
  return (person: ShownPerson) => {
    const to = pageOf(person)
    if (to) navigate(to)
    else showProfile(person)
  }
}
